import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Lightbulb, PenTool, Code, Rocket } from 'lucide-react';

const Process = () => {
  const { t } = useLanguage();

  // Steps are created inside the render to get updated translations
  const getSteps = () => [
    { num: '01', icon: Lightbulb, title: t('process.step1'), desc: t('process.step1Desc') },
    { num: '02', icon: PenTool, title: t('process.step2'), desc: t('process.step2Desc') },
    { num: '03', icon: Code, title: t('process.step3'), desc: t('process.step3Desc') },
    { num: '04', icon: Rocket, title: t('process.step4'), desc: t('process.step4Desc') },
  ];

  const steps = getSteps();

  return (
    <section
      id="process"
      data-testid="process-section"
      className="py-24 md:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <div className="h-px w-8 bg-[#B8860B]"></div>
            <span className="mx-3 text-[#B8860B] text-xs tracking-[0.2em] uppercase">{t('nav.process')}</span>
            <div className="h-px w-8 bg-[#B8860B]"></div>
          </div>
          <h2
            data-testid="process-title"
            className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight"
          >
            {t('process.title')}
          </h2>
        </div>

        {/* Horizontal Timeline */}
        <div className="relative">
          {/* Connection Line - Desktop */}
          <div className="hidden lg:block absolute top-[60px] left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-[#B8860B]/30 to-transparent"></div>

          {/* Steps Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {steps.map((step, index) => (
              <div
                key={index}
                className="relative group"
                data-testid={`process-step-${index}`}
              >
                {/* Step Card */}
                <div className="bg-black border border-white/10 rounded-xl p-6 md:p-8 transition-all duration-300 hover:border-[#B8860B]/50 hover:-translate-y-1">
                  {/* Step Number & Icon */}
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-4xl font-light text-[#B8860B]/30 font-outfit">
                      {step.num}
                    </span>
                    <div className="relative">
                      <div className="absolute inset-0 bg-[#B8860B]/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative w-12 h-12 bg-[#B8860B]/10 rounded-full flex items-center justify-center">
                        <step.icon size={24} strokeWidth={1.5} className="text-[#B8860B]" />
                      </div>
                    </div>
                  </div>

                  {/* Pulse Dot - Timeline Indicator */}
                  <div className="hidden lg:block absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="relative">
                      <div className="w-3 h-3 bg-[#B8860B] rounded-full"></div>
                      <div className="absolute inset-0 w-3 h-3 bg-[#B8860B] rounded-full animate-ping opacity-50"></div>
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="font-outfit text-xl font-medium text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-[#A1A1AA] text-sm leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
